import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { isEmpty, get, debounce, cloneDeep, set } from 'lodash';

// React view component to render
import { siteHeader as SiteHeaderView, siteHeaderPreloader as SiteHeaderPreloader } from 'ui/views/widgets/siteHeader';
import { HeaderService } from 'services/headerAndFooter';
import { REDUCER, COMPONENT_NAMES } from 'constants/entity-names';
import { addComponentData } from 'store/actions/addComponentData';
import { SERVICE_RESPONSE_STATUS, SIZES, GLOBAL_LABELS } from 'constants/common';
import { updateAppContext } from 'store/actions/updateAppContext';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import * as successOverlay from 'utils/successOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { getLabel } from 'utils/getLabel';
import * as ROUTE_PATHS from 'constants/route-paths';

/**
 * @type {class}
 * @desc Header container component for global site header.
 * @example
 * import { SiteHeader } from 'ui/containers/SiteHeader';
 *
 * // render
 * <SiteHeader stuck={ true }/>
 */
class _SiteHeader extends React.Component {
    constructor( props ) {
        super( props );

        // bind events and methods
        this.toggleSticky = debounce( this.toggleSticky, 100 ).bind( this );
        this.handleEditEpostIdClick = this.handleEditEpostIdClick.bind( this );
        this.handleLogoutFromHeader = this.handleLogoutFromHeader.bind(this);
        this.submitEpostId = this.submitEpostId.bind( this );

        // component state
        this.state = {
            isFetching: false,
            networkStatus: get( props, 'headerInfo.status', undefined ),
            isSticky: this.shouldSticky(),
        };

        // header and footer service instance
        this.headerService = new HeaderService();

        // load header information if not present in the store
        if( isEmpty( props.headerInfo ) ) {
            this.state.isFetching = true;
            this.fetchHeaderInfo();
        }
    }

    async handleLogoutFromHeader(url) {
        this.headerService.cancel();

        pageLevelPreloader.show()
        try {
            const data = await this.headerService.handleLogout({url, data: {}});
            // check if response status was success
            if( 200 != data.status ) {
                
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
                pageLevelPreloader.hide();
            } else {
                // window.location.assign('https://portal-ext.dev-healthdyne.com/unauthenticated');
                    this.props.history.replace(ROUTE_PATHS.SSO_LOGOUT);
                    pageLevelPreloader.hide();
                
            }
        } catch( e ) {
            pageLevelPreloader.hide();
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    /**
     * @desc Fetch and cache site header information.
     */
    async fetchHeaderInfo() {
        log.debug( 'SiteHeader.fetchHeaderInfo()' );

        // cancel ongoing request
        this.headerService.cancel();

        try {
            const data = await this.headerService.postData();

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== data.status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                }, () => {
                    // update header info into the store
                    this.props.updateSiteHeaderAction( {
                        componentName: COMPONENT_NAMES.SITE_HEADER,
                        componentData: data,
                    } );
                } );
            }

        } catch( e ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE,
            } );
        }
    }

    /**
     * @desc Check if header should be sticky.
     */
    shouldSticky() {
        return 50 < window.scrollY; // if `window` has scrolled more than `50px`
    }

    /**
     * @desc Toggle sticky behaviour of the header.
     */
    toggleSticky() {
        log.debug( 'SiteHeader.toggleSticky()' );
        
        // get sticky value
        const isSticky = this.shouldSticky();

        // if `isSticky` value is different, update the state
        if( isSticky !== this.state.isSticky ) {
            this.setState( {
                isSticky,
            } );
        }
    }

    /**
     * @desc When edit ePost navigation action is clicked.
     */
    handleEditEpostIdClick() {
        log.debug( 'SiteHeader.handleEditEpostIdClick()' );

        overlay.open( {
            settings: {
                modalType: OVERLAY_VIEWS.SUBMIT_AUTH_EPOST_ID,
                showCloseButton: false,
                ignoreOutsideClick: true,
            },
            config: {},
            data: {
                mode: 'edit',
                title: get( this.props.headerInfo, 'metaData.authorization.title', {} ),
                fields: get( this.props.headerInfo, 'metaData.authorization.fields', [] ),
                actions: get( this.props.headerInfo, 'metaData.authorization.actions', [] ),
                values: {
                    ePostId: get( this.props.appContext, 'component.data.state.authorization.ePostId' ),
                },
                onSubmit: this.submitEpostId,
            },
        } );
    }


    // submit ePost ID
    async submitEpostId( { values, url } = {} ) {
        log.debug( 'CheckAppContextHoc.submitEpostId()' );

        // get `ePostId` from form values
        const ePostId = get( values, 'ePostId', null );

        // cancel ongoing request
        this.headerService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status, data } = await this.headerService.submitEpostId( {
            url: url,
            data: {
                data: {
                    ePostId: ePostId,
                }
            }
        } );

        // show page level preloader
        pageLevelPreloader.hide();

        // on success, save `ePostId` in redux store
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {

            // close overlay form
            overlay.close();

            // update `appContext` in redux store
            const appContext = cloneDeep( this.props.appContext );
            set( appContext, 'component.data.state.authorization.ePostId', ePostId );
            this.props.updateAppContext( appContext );

            // display success message
            successOverlay.open( {
                message: getLabel( GLOBAL_LABELS.DISPLAY_TEXT_DONE_SUCCESSFULLY ),
            } );
        }  else {
            
            // show error dialog
            if ( ! isEmpty( data.description ) ) {
                errorOverlay.open( { message: data.description, size: SIZES.SMALL } );
            } else {
                errorOverlay.open();
            }
        }
    }

    /************************************/

    /**
     * @desc When component mounts, listen to events.
     */
    componentDidMount() {
        window.addEventListener( 'scroll', this.toggleSticky ); // add `scroll` event
        window.addEventListener( 'resize', this.toggleSticky ); // add `resize` event
    }

    /**
     * @desc When component unmounts, remove scroll listener.
     */
    componentWillUnmount() {
        this.headerService.cancel(); // cancel ongoing request
        window.removeEventListener( 'scroll', this.toggleSticky ); // remove `scroll` events
        window.removeEventListener( 'resize', this.toggleSticky ); // remove `resize` events
    }

    /**
     * @desc Render header view.
     */
    render() {
        if( this.state.isFetching ) {
            return (
                <SiteHeaderPreloader />
            );
        } else if( ! isEmpty( this.props.headerInfo ) ) {
            return (
                <SiteHeaderView
                    { ...this.props }
                    handleLogoutFromHeader={ this.handleLogoutFromHeader }
                    { ...this.props.headerInfo }
                    isSticky={ this.state.isSticky }
                    onEditEpostIdClick={ this.handleEditEpostIdClick }
                />
            );
        }

        return null; // return no view
    }
}

// set display name
_SiteHeader.displayName = '_SiteHeader';

// set default props
_SiteHeader.defaultProps = {};

// map state to props
const mapStateToProps = ( state ) => {
    return {
        headerInfo: get( state, `${ REDUCER.COMPONENT_DATA }.${ COMPONENT_NAMES.SITE_HEADER }`, undefined ),
        appContext: state[ REDUCER.APP_CONTEXT ],
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        updateSiteHeaderAction: bindActionCreators( addComponentData, dispatch ),
        updateAppContext: bindActionCreators( updateAppContext, dispatch ),
    };
};

// return component with acccess to Redux store
export const SiteHeader = connect( mapStateToProps, mapDispatchToProps )( _SiteHeader );
